// src/portal-app/team/rolesShared.js
//
// Shared primitives for the Roles + Users surface — the screen-local
// stylesheet, avatar helpers and the Escape-key hook.
//
// Extracted out of RolesScreen.js in the Plan 4 Task 20 commit (b61bf97),
// together with the P4 migration rather than as a separate extraction
// commit first — see RolesScreen.js's "FILE SPLIT" header note for why.
// RolesScreen.js was 1313 lines — the second-largest file in the app —
// before any P4 work. Split along the same boundary
// operations/reservationsShared.js (Task 7) established:
//
//   rolesShared.js           CSS + pure helpers (this file)
//   RoleForms.js             every write path (role form, user form,
//                            reset-password form)
//   RolePermissionEditor.js  the permission grid — its own fetch, its own
//                            save/reset, isolated because it is the single
//                            most permission-sensitive piece on this screen
//   RoleRows.js              the read path (one role row / one user row)
//   RolesScreen.js           state, fetching and layout only
//
// This module IS a verbatim move for the icon markup and the useEscape
// hook — diff against
// `git show b61bf97^:frontend/src/portal-app/team/RolesScreen.js` to
// verify. The CSS text below is NOT byte-identical (new hero/control-row/
// filter-summary/count-line/role-wrap rules were added for the P4 layout;
// dead pre-migration rules like .rl-role-card/.rl-add-btn were dropped —
// see the CSS block's own comments for what changed and why). `initials()`
// is unchanged; the pre-split `avatarVar`/`AVATAR_VARS` pair was NOT moved
// here — it was replaced by a new `avatarTone()` in RoleRows.js (see that
// file's header, and "Identity helpers" below for what carried over).
//
// Nothing here reads context or calls a service: these are pure functions
// and constants, importable from any of the other four modules without a
// cycle.
import React from 'react';

/* ------------------------------------------------------------------ */
/* Screen-local stylesheet                                             */
/* ------------------------------------------------------------------ */
const CSS = `
/* ---- layout ---- */
.rl-wrap{width:100%;max-width:680px;margin:0 auto;box-sizing:border-box;
  padding:8px 0 120px;}
.rl-seg{padding:0 16px;margin-bottom:14px;}

/* ---- hero — summarises exactly the roles rendered below it ---- */
.rl-hero{margin:0 16px 16px;padding:18px 20px 16px;}

/* ---- control row — FilterButton only (Users tab) ---- */
.rl-control-row{display:flex;justify-content:flex-end;padding:0 16px;
  margin-bottom:10px;}

/* ---- active-filter summary ---- */
.rl-filtersum{display:flex;align-items:center;gap:12px;
  margin:4px 16px 12px;}
.rl-filtersum-text{flex:1 1 auto;min-width:0;font-size:12px;font-weight:600;
  color:var(--c-textLight);overflow:hidden;text-overflow:ellipsis;
  white-space:nowrap;}
.rl-filtersum-clear{flex:none;min-height:44px;padding-inline:6px;border:none;
  background:transparent;font-family:inherit;font-size:12px;font-weight:700;
  color:var(--c-green);cursor:pointer;}

/* ---- search (Users tab) ---- */
.rl-search-wrap{padding:0 16px;margin-bottom:10px;}

/* ---- count line (Users tab) ---- */
.rl-count-line{font-size:11px;font-weight:700;text-transform:uppercase;
  letter-spacing:1.2px;color:var(--c-textLight);margin:2px 16px 10px;}

/* ---- role list — CondensedRow + actions row beneath it ---- */
.rl-list{display:flex;flex-direction:column;gap:10px;padding:0 16px;}
.rl-role-wrap{display:flex;flex-direction:column;gap:6px;}
.rl-role-actions{display:flex;gap:6px;margin-inline-start:44px;}
.rl-icon-btn{display:inline-flex;align-items:center;justify-content:center;
  min-width:44px;min-height:44px;border:none;border-radius:10px;
  background:color-mix(in srgb,var(--c-ink) 6%,transparent);
  color:var(--c-textLight);cursor:pointer;
  transition:background .15s ease,color .15s ease;}
.rl-icon-btn:active{background:color-mix(in srgb,var(--c-ink) 12%,transparent);}
.rl-icon-btn:disabled{opacity:.3;cursor:not-allowed;}
.rl-icon-btn-danger:not(:disabled):hover{background:color-mix(in srgb,var(--c-red) 10%,transparent);
  color:var(--c-red);}

/* ---- permission editor ---- */
.rl-perm-editor{margin:12px 16px 0;border-radius:20px;overflow:hidden;}
.rl-perm-header{display:flex;align-items:center;gap:8px;
  padding:12px 16px 8px;}
.rl-perm-title{font-size:13px;font-weight:700;color:var(--c-ink);flex:1;}
.rl-perm-category{padding:0 16px;}
.rl-perm-cat-label{font-size:10px;font-weight:700;letter-spacing:.8px;
  text-transform:uppercase;color:var(--c-textLight);
  padding:8px 0 4px;}
.rl-perm-row{display:flex;align-items:center;min-height:48px;
  padding:6px 0;gap:12px;
  border-bottom:1px solid color-mix(in srgb,var(--c-ink) 6%,transparent);}
.rl-perm-row:last-child{border-bottom:none;}
.rl-perm-label{flex:1;font-size:13px;font-weight:600;color:var(--c-ink);}
.rl-perm-toggles{display:flex;gap:16px;}
.rl-perm-toggle-col{display:flex;flex-direction:column;align-items:center;gap:3px;}
.rl-perm-toggle-label{font-size:9px;font-weight:700;text-transform:uppercase;
  letter-spacing:.6px;color:var(--c-textLight);}
.rl-checkbox{width:20px;height:20px;cursor:pointer;flex:none;
  accent-color:var(--c-green);}

/* ---- dirty bar ---- */
.rl-dirty-bar{position:sticky;bottom:calc(80px + var(--safe-bottom,0px));
  z-index:50;margin:12px 16px 0;border-radius:18px;
  padding:12px 16px;display:flex;align-items:center;gap:10px;
  background:color-mix(in srgb,var(--c-green) 90%,transparent);
  box-shadow:0 4px 20px color-mix(in srgb,var(--c-green) 30%,transparent);}
.rl-dirty-msg{flex:1;font-size:13px;font-weight:700;color:var(--c-white);}
.rl-dirty-reset{min-height:36px;padding:0 14px;border-radius:10px;
  border:1.5px solid color-mix(in srgb,var(--c-white) 50%,transparent);
  background:transparent;font-family:inherit;font-size:13px;font-weight:700;
  color:var(--c-white);cursor:pointer;
  transition:background .15s ease;}
.rl-dirty-reset:active{background:color-mix(in srgb,var(--c-white) 15%,transparent);}
.rl-dirty-save{min-height:36px;padding:0 16px;border-radius:10px;
  border:none;background:var(--c-white);
  font-family:inherit;font-size:13px;font-weight:700;
  color:var(--c-green);cursor:pointer;
  display:inline-flex;align-items:center;gap:6px;
  transition:opacity .15s ease;}
.rl-dirty-save:disabled{opacity:.6;cursor:not-allowed;}

/* ---- users list — unchanged custom card (see RoleRows.js UserRow) ---- */
.rl-users-list{display:flex;flex-direction:column;gap:10px;padding:0 16px;}
.rl-user-card{padding:14px 16px;cursor:default;}
.rl-user-header{display:flex;align-items:flex-start;gap:10px;margin-bottom:8px;}
.rl-user-avatar{width:38px;height:38px;border-radius:50%;flex:none;
  display:flex;align-items:center;justify-content:center;
  font-size:13px;font-weight:700;color:var(--c-white);}
.rl-user-info{flex:1;min-width:0;}
.rl-user-name{font-size:14px;font-weight:800;color:var(--c-ink);
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.rl-user-username{font-size:12px;font-weight:500;color:var(--c-textLight);}
.rl-user-footer{display:flex;align-items:center;gap:8px;flex-wrap:wrap;
  margin-top:6px;}
.rl-user-role{font-size:11px;font-weight:600;color:var(--c-textLight);}
.rl-user-actions{display:flex;gap:6px;margin-inline-start:auto;}

/* ---- FAB — shared by "Add Role" and "Add User" (see file header:        */
/*      promoted the pre-migration inline dashed "Add Role" button to a    */
/*      FAB so it renders OUTSIDE SurfaceStates and stays reachable even   */
/*      when the role list is empty, matching "Add User"'s pre-existing    */
/*      FAB placement). ---- */
.rl-fab{position:fixed;bottom:calc(80px + var(--safe-bottom,0px));
  inset-inline-end:20px;z-index:200;
  display:inline-flex;align-items:center;justify-content:center;gap:8px;
  min-height:52px;min-width:52px;padding:0 20px;border-radius:26px;border:none;
  background:var(--c-green);
  font-family:inherit;font-size:14px;font-weight:700;color:var(--c-white);
  cursor:pointer;
  box-shadow:0 4px 20px color-mix(in srgb,var(--c-green) 40%,transparent);
  transition:transform .15s ease,box-shadow .15s ease;}
.rl-fab:active{transform:scale(.97);}

/* ---- glass sheet backdrop ---- */
.rl-sheet-backdrop{position:fixed;inset:0;z-index:1000;
  background:color-mix(in srgb,var(--c-ink) 45%,transparent);
  display:flex;align-items:flex-end;}
.rl-sheet{width:100%;max-width:560px;margin:0 auto;
  padding:24px 20px calc(24px + var(--safe-bottom,0px));
  box-sizing:border-box;border-radius:24px 24px 0 0;
  max-height:85vh;overflow-y:auto;}
.rl-drag{width:36px;height:4px;border-radius:2px;margin:0 auto 20px;
  background:color-mix(in srgb,var(--c-ink) 20%,transparent);}
.rl-sheet-title{font-size:18px;font-weight:800;letter-spacing:-.3px;
  color:var(--c-ink);margin-bottom:16px;}
.rl-field{margin-bottom:14px;}
.rl-field-label{display:block;font-size:11px;font-weight:700;
  text-transform:uppercase;letter-spacing:.8px;color:var(--c-textLight);
  margin-bottom:5px;}
.rl-field-input{display:block;width:100%;box-sizing:border-box;
  min-height:44px;padding:10px 14px;border-radius:14px;
  border:1.5px solid color-mix(in srgb,var(--c-ink) 12%,transparent);
  background:color-mix(in srgb,var(--c-surface) 60%,transparent);
  font-family:inherit;font-size:14px;font-weight:500;color:var(--c-ink);
  outline:none;}
.rl-field-input:focus{border-color:var(--c-green);}
.rl-field-select{appearance:none;cursor:pointer;}
.rl-error{padding:10px 14px;border-radius:12px;margin-bottom:12px;
  background:color-mix(in srgb,var(--c-red) 8%,transparent);
  border:1.5px solid color-mix(in srgb,var(--c-red) 25%,transparent);
  font-size:13px;font-weight:600;color:var(--c-red);}
.rl-actions{display:flex;gap:10px;margin-top:6px;}
.rl-btn-cancel{flex:1;min-height:44px;border:1.5px solid
  color-mix(in srgb,var(--c-ink) 14%,transparent);border-radius:16px;
  background:color-mix(in srgb,var(--c-surface) 50%,transparent);
  font-family:inherit;font-size:15px;font-weight:700;
  color:var(--c-ink);cursor:pointer;
  transition:transform .15s ease;}
.rl-btn-cancel:active{transform:scale(.97);}
.rl-btn-save{flex:1;min-height:44px;border:none;border-radius:16px;
  background:var(--c-green);
  font-family:inherit;font-size:15px;font-weight:700;
  color:var(--c-white);cursor:pointer;
  display:inline-flex;align-items:center;justify-content:center;gap:8px;
  transition:transform .15s ease,opacity .15s ease;}
.rl-btn-save:active{transform:scale(.97);}
.rl-btn-save:disabled{opacity:.5;cursor:not-allowed;transform:none;}
.rl-spinner{width:16px;height:16px;border-radius:50%;flex:none;
  border:2.5px solid color-mix(in srgb,var(--c-white) 30%,transparent);
  border-top-color:var(--c-white);
  animation:rlSpin .75s linear infinite;}
@keyframes rlSpin{to{transform:rotate(360deg);}}

/* ---- slide-up ---- */
@keyframes rlSlideUp{
  from{transform:translateY(100%);opacity:0;}
  to{transform:translateY(0);opacity:1;}
}
.rl-sheet-enter{animation:rlSlideUp 260ms ease-out both;}
@keyframes rlFadeIn{from{opacity:0;}to{opacity:1;}}
.rl-backdrop-enter{animation:rlFadeIn 200ms ease both;}

@media(prefers-reduced-motion:reduce){
  .rl-sheet-enter,.rl-backdrop-enter{
    animation:none;transition:none;}
  .rl-spinner{animation-duration:1.5s;}
}
`;

let cssInjected = false;
export function ensureRlCss() {
    if (cssInjected || typeof document === 'undefined') return;
    const tag = document.createElement('style');
    tag.id = 'rl-css';
    tag.textContent = CSS;
    document.head.appendChild(tag);
    cssInjected = true;
}

/* ------------------------------------------------------------------ */
/* Identity helpers — initials + tone-from-name (Users tab avatar).    */
/* Same charCode-modulo-FALLBACK_TONES formula EmployeesScreen's        */
/* avatarTone() uses, so the palette matches the rest of the app        */
/* instead of a private AVATAR_VARS copy.                               */
/* ------------------------------------------------------------------ */
export function initials(name = '') {
    return name.trim().split(/\s+/).map((w) => w[0] || '').join('').slice(0, 2).toUpperCase();
}

/* ------------------------------------------------------------------ */
/* SVG Icons not already in shell/kit/icons.js                         */
/* ------------------------------------------------------------------ */
export const ShieldIcon = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="1.8"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
);

export const TrashIcon = ({ size = 15 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        <path d="M10 11v6" />
        <path d="M14 11v6" />
        <path d="M9 6V4h6v2" />
    </svg>
);

export const KeyIcon = ({ size = 15 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
    </svg>
);

export const UserCheckIcon = ({ size = 15 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <polyline points="16 11 18 13 22 9" />
    </svg>
);

/* ------------------------------------------------------------------ */
/* Escape-key hook — used by every write-path sheet.                   */
/* ------------------------------------------------------------------ */
export function useEscape(active, onEscape) {
    React.useEffect(() => {
        if (!active) return undefined;
        const handler = (e) => { if (e.key === 'Escape') onEscape(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [active, onEscape]);
}
